import React, { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl';
import type { ReferenceLayerView } from '../../hooks/useReferenceLayers';
import {
  REFERENCE_CATEGORICAL_PALETTE,
  REFERENCE_COLOR_RAMPS,
  type ReferenceFieldStat,
  type ReferenceLayerDto,
  type ReferenceLayerStyle,
} from '../../services/referenceLayerApi';

export const REF_LAYER_PREFIX = 'ref-layer-';

type MapboxExpression = unknown[];

/** Map layer ids for one reference layer (fill / line / point / label). */
export function referenceLayerMapIds(id: number) {
  const base = `${REF_LAYER_PREFIX}${id}`;
  return {
    source: base,
    fill: `${base}-fill`,
    line: `${base}-line`,
    point: `${base}-point`,
    label: `${base}-label`,
  };
}

/** Which reference layer (id) a clicked map layer belongs to, or null. */
export function referenceLayerIdFromMapLayer(mapLayerId: string | undefined | null): number | null {
  if (!mapLayerId || !mapLayerId.startsWith(REF_LAYER_PREFIX)) return null;
  const rest = mapLayerId.slice(REF_LAYER_PREFIX.length);
  const n = Number.parseInt(rest, 10);
  return Number.isFinite(n) ? n : null;
}

/** Clickable layer ids for `interactiveLayerIds` (fill + line + point for every enabled layer). */
export function referenceLayerInteractiveIds(views: ReferenceLayerView[]): string[] {
  const out: string[] = [];
  for (const v of views) {
    if (!v.enabled || !v.collection) continue;
    const ids = referenceLayerMapIds(v.layer.id);
    out.push(ids.fill, ids.line, ids.point);
  }
  return out;
}

/**
 * Mapbox colour expression for a layer: solid colour, a numeric ramp
 * (interpolate over the field's min/max) or a categorical match.
 */
export function referenceColorExpression(
  style: ReferenceLayerStyle,
  fields: Record<string, ReferenceFieldStat>,
  base: string
): string | MapboxExpression {
  const cb = style.color_by;
  if (!cb || !cb.field) return base;
  const stat = fields[cb.field];
  if (!stat) return base;
  if (cb.mode === 'ramp' && stat.kind === 'number' && stat.min != null && stat.max != null) {
    const ramp = REFERENCE_COLOR_RAMPS[cb.ramp] || REFERENCE_COLOR_RAMPS.viridis;
    const colors = cb.reverse ? [...ramp].reverse() : ramp;
    const min = stat.min;
    const max = stat.max;
    if (max <= min) return colors[colors.length - 1];
    const stops: unknown[] = [];
    colors.forEach((c, i) => {
      stops.push(min + ((max - min) * i) / (colors.length - 1), c);
    });
    return ['interpolate', ['linear'], ['to-number', ['get', cb.field], min], ...stops];
  }
  // Categorical (explicit, or a numeric field without a usable range).
  const values = stat.values;
  if (!values || values.length === 0) return base;
  const palette = cb.reverse ? [...REFERENCE_CATEGORICAL_PALETTE].reverse() : REFERENCE_CATEGORICAL_PALETTE;
  const expr: unknown[] = ['match', ['to-string', ['get', cb.field]]];
  values.forEach((v, i) => {
    expr.push(v, palette[i % palette.length]);
  });
  expr.push(base);
  return expr;
}

/** Legend entries derived from the same rules the map uses. */
export function referenceLegend(
  layer: ReferenceLayerDto,
  style: ReferenceLayerStyle
): Array<{ label: string; color: string }> {
  const cb = style.color_by;
  if (!cb) return [];
  const stat = layer.fields[cb.field];
  if (!stat) return [];
  if (cb.mode === 'ramp' && stat.kind === 'number' && stat.min != null && stat.max != null) {
    const ramp = REFERENCE_COLOR_RAMPS[cb.ramp] || REFERENCE_COLOR_RAMPS.viridis;
    const colors = cb.reverse ? [...ramp].reverse() : ramp;
    const fmt = (n: number) => (Math.abs(n) >= 1000 ? Math.round(n).toLocaleString() : Number(n.toFixed(2)).toString());
    return colors.map((color, i) => ({
      color,
      label: fmt(stat.min! + ((stat.max! - stat.min!) * i) / (colors.length - 1)),
    }));
  }
  const values = stat.values || [];
  const palette = cb.reverse ? [...REFERENCE_CATEGORICAL_PALETTE].reverse() : REFERENCE_CATEGORICAL_PALETTE;
  return values.map((v, i) => ({ label: v, color: palette[i % palette.length] }));
}

type Props = {
  layers: ReferenceLayerView[];
  /** Insert below this map layer (e.g. device markers) so reference data never covers tracks. */
  beforeId?: string;
};

const POLYGON_FILTER: MapboxExpression = ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']];
const LINEISH_FILTER: MapboxExpression = [
  'any',
  ['==', ['geometry-type'], 'Polygon'],
  ['==', ['geometry-type'], 'MultiPolygon'],
  ['==', ['geometry-type'], 'LineString'],
  ['==', ['geometry-type'], 'MultiLineString'],
];
const POINT_FILTER: MapboxExpression = ['any', ['==', ['geometry-type'], 'Point'], ['==', ['geometry-type'], 'MultiPoint']];

const OneLayer: React.FC<{ view: ReferenceLayerView; beforeId?: string }> = ({ view, beforeId }) => {
  const { layer, style, collection } = view;
  const ids = referenceLayerMapIds(layer.id);
  const fillColor = useMemo(() => referenceColorExpression(style, layer.fields, style.color), [style, layer.fields]);
  const strokeColor = useMemo(
    () => (style.color_by ? referenceColorExpression(style, layer.fields, style.stroke_color) : style.stroke_color),
    [style, layer.fields]
  );
  if (!collection) return null;
  const minzoom = Math.max(0, Math.min(22, style.min_zoom || 0));
  const labelField = layer.label_field;
  const isLine = layer.geometry_kind === 'line';
  return (
    <Source id={ids.source} type="geojson" data={collection} generateId>
      <Layer
        id={ids.fill}
        type="fill"
        filter={POLYGON_FILTER as never}
        minzoom={minzoom}
        beforeId={beforeId}
        paint={{
          'fill-color': fillColor as never,
          'fill-opacity': style.fill_opacity,
        }}
      />
      <Layer
        id={ids.line}
        type="line"
        filter={LINEISH_FILTER as never}
        minzoom={minzoom}
        beforeId={beforeId}
        layout={{ 'line-join': 'round', 'line-cap': 'round' }}
        paint={{
          'line-color': strokeColor as never,
          'line-width': style.stroke_width,
          'line-opacity': style.stroke_opacity,
        }}
      />
      <Layer
        id={ids.point}
        type="circle"
        filter={POINT_FILTER as never}
        minzoom={minzoom}
        beforeId={beforeId}
        paint={{
          'circle-color': fillColor as never,
          'circle-radius': style.point_radius,
          'circle-opacity': style.point_opacity,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1,
          'circle-stroke-opacity': Math.min(1, style.point_opacity + 0.1),
        }}
      />
      {style.labels && labelField && (
        <Layer
          id={ids.label}
          type="symbol"
          minzoom={Math.max(minzoom, 9)}
          beforeId={beforeId}
          layout={{
            'text-field': ['to-string', ['get', labelField]],
            'text-size': style.label_size,
            'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
            'symbol-placement': isLine ? 'line' : 'point',
            'text-anchor': layer.geometry_kind === 'point' ? 'top' : 'center',
            'text-offset': layer.geometry_kind === 'point' ? [0, 0.8] : [0, 0],
            'text-max-width': 10,
            'text-allow-overlap': false,
            'text-optional': true,
          }}
          paint={{
            'text-color': '#ffffff',
            'text-halo-color': 'rgba(0,0,0,0.85)',
            'text-halo-width': 1.4,
          }}
        />
      )}
    </Source>
  );
};

/** Draws every enabled reference layer (fill + stroke + points + optional labels). */
const ReferenceLayersMapLayers: React.FC<Props> = ({ layers, beforeId }) => (
  <>
    {layers.map((view) =>
      view.enabled && view.collection ? <OneLayer key={view.layer.id} view={view} beforeId={beforeId} /> : null
    )}
  </>
);

export default ReferenceLayersMapLayers;
